import { bell, crack, figure, groundRing, lotus, palm, shards, star, waves } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ฝ่ามือก้องระฆัง (หินระฆัง, hard): the bell-caster's inner palm. Bronze-gold palms, cast bells, grey
 * inscribed stone and the hum drawn as rings of sound; no flames, no blood.
 */

const GOLD = '#ffcf5a';
const GOLD_LINE = '#c8862a';
const BRONZE = '#d89a48';
const BRONZE_DK = '#8a5a2a';
const HUM = '#fff0a8';
const STONE = '#a49e92';
const STONE_HI = '#d8d2c4';

const goldPalm = (p: Pix, x: number, y: number, o: { k?: number; flip?: boolean; flipY?: boolean } = {}) =>
  palm(p, x, y, GOLD, { ...o, lines: GOLD_LINE });

/** An inscribed stone tablet (stele) with carved lines. */
function stele(p: Pix, x: number, y: number, w: number, h: number): void {
  p.rect(x, y + 2, w, h - 2, STONE);
  p.ell(x + w / 2 - 0.5, y + 2, w / 2, 2.5, STONE);
  for (let j = y + 4; j < y + h - 2; j += 3) p.rect(x + 2, j, w - 4, 1, STONE_HI);
}

registerIcons('skill', {
  // A golden palm sends the bell's hum far out in rings.
  'pp-wave': (p) => {
    waves(p, 13, 16, 5, 4, HUM, 0.9, 4);
    goldPalm(p, 1, 9);
  },
  // The palm comes down like a hammer on carved stone and splits it.
  'pp-shatter': (p) => {
    stele(p, 6, 13, 13, 17);
    crack(p, [
      [12, 14],
      [10, 19],
      [13, 22],
      [11, 29],
    ], '#4a4038', 2);
    goldPalm(p, 8, 0, { flipY: true });
    shards(p, 12, 18, 11, STONE_HI, 5, 0.6);
  },
  // A thousand hands: three palms struck in a row, each fainter behind.
  'pp-thousand': (p) => {
    palm(p, 2, 4, BRONZE_DK);
    palm(p, 8, 8, BRONZE);
    goldPalm(p, 14, 12);
    p.rect(26, 6, 3, 1, HUM).rect(28, 10, 2, 1, HUM);
  },
  // The binding palm: a bell drops over a small foe, pinning it still.
  'pp-calm': (p) => {
    figure(p, 13, 19, STONE_HI, 'stand');
    bell(p, 16, 9, 15, BRONZE, { band: GOLD_LINE, w: 14 });
    for (let y = 18; y < 24; y++) for (let x = 11; x < 21; x++) if ((x + y) % 2 === 0) p.erase(x, y);
    goldPalm(p, 19, 0, { flipY: true });
    waves(p, 16, 22, 11, 2, HUM, 0.5, 3);
  },
  // A palm on the ground; a ring of tremors spreads round with spoke cracks.
  'pp-dharma': (p) => {
    groundRing(p, 16, 23, 13, 6, BRONZE);
    for (let k = 0; k < 8; k++) {
      const a = (k * Math.PI) / 4;
      p.line(16 + Math.cos(a) * 3, 23 + Math.sin(a) * 1.5, 16 + Math.cos(a) * 11, 23 + Math.sin(a) * 5, GOLD_LINE);
    }
    goldPalm(p, 10, 6, { flipY: true });
  },
  // Seated on a golden lotus, mending.
  'pp-lotus': (p) => {
    lotus(p, 17, 25, 22, GOLD, HUM);
    figure(p, 14, 11, STONE_HI, 'sit', { head: C.skin });
    p.ring(17, 12, 6, 6, HUM, -Math.PI, 0);
  },
  // The vow: a still figure under a ringing bell, a shackle broken off at its feet.
  'pp-vow': (p) => {
    bell(p, 18, 6, 20, BRONZE, { band: GOLD_LINE });
    p.ell(18, 15, 3, 3, HUM);
    p.ring(6, 26, 3, 2, C.steelMid);
    p.ring(12, 27, 3, 2, C.steelMid);
    p.erase(9, 26).erase(9, 27);
    star(p, 27, 5, 2, HUM);
  },
  // The great golden palm comes down over all around it, inside a bell's ring.
  'pp-tathagata': (p) => {
    p.ring(16, 18, 13, 11, BRONZE, Math.PI, Math.PI * 2);
    groundRing(p, 16, 25, 12, 4, BRONZE);
    goldPalm(p, 4, 0, { k: 2, flipY: true });
    star(p, 4, 22, 2, HUM);
    star(p, 28, 21, 2, HUM);
  },
});
